import {
  Skeleton, SkeletonButton, SkeletonInput, SkeletonRegion, SkeletonTable,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';

import { PANEL_PAD, STACK } from '../../constants/density';
import { SetupRowSkeleton } from './RowSkeleton';

/**
 * The pieces every workbench skeleton is made of.
 *
 * Two pages draw this shape — «مدیریت هزینه‌ها» and «کالاها و خدمات», the halves
 * of the old «عملیات و هزینه» — and «گزارش‌ها» draws a near-relative. Each has
 * its own view specs and its own title, but the rail's footprint, the toolbar
 * above a dense table, and the heading-plus-add-form of a setup screen are the
 * SAME geometry. A skeleton that is a few pixels off the page it stands in for
 * makes the content jump when it arrives, which is the one thing a skeleton
 * exists to prevent — so the geometry is written once.
 *
 * Everything here imports only the kit, the tokens and the density scale: these
 * are loaded EAGERLY as a router fallback, so they can never reach into a
 * screen's chunk.
 */

/** The segmented window picker: four options in a padded track. */
export function WindowPicker({ height = 36 }) {
  // The kit `SegmentedControl`: 36px options (44 at `lg`) in a 4px-padded track.
  return (
    <div aria-hidden style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, background: C.bg }}>
      {[64, 72, 68, 48].map((w, i) => <Skeleton key={i} width={w} height={height} radius={9} />)}
    </div>
  );
}

/**
 * The object rail's footprint.
 *
 * `groups` is how many entries each rail group holds, so a page whose rail is
 * two-plus-three does not draw a placeholder for a five-entry one and then
 * shrink.
 */
export function RailSkeleton({ groups = [3, 4] }) {
  return (
    <nav aria-hidden style={{ width: 176, flexShrink: 0, borderInlineEnd: `1px solid ${C.border}`, background: C.card, padding: '12px 8px' }}>
      {groups.map((count, gi) => (
        <div key={gi} style={{ marginTop: gi ? 18 : 0 }}>
          <div style={{ padding: '0 10px', marginBottom: 6 }}><Skeleton width={64} height={11} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from({ length: count }, (_, i) => (
              <div key={i} style={{ padding: '9px 10px' }}>
                <Skeleton width={[72, 56, 64, 80][i % 4]} height={13} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ height: STACK.regular }} />
    </nav>
  );
}

/** A dense table under its own toolbar — the shape of a «کار روزانه» view. */
export function TableSurface({ spec, label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div aria-hidden style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <Skeleton width={spec.filters.search} height={44} radius={RADIUS.control} />
        {spec.filters.select ? <Skeleton width={spec.filters.select} height={44} radius={RADIUS.control} /> : null}
        {Array.from({ length: spec.filters.chips }, (_, i) => (
          <Skeleton key={i} width={[52, 96, 88][i % 3]} height={36} radius={RADIUS.pill} />
        ))}
        <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Skeleton width={56} height={12} />
          <SkeletonButton size="sm" width={72} />
          <SkeletonButton size="sm" width={96} />
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', padding: '12px 20px 20px' }}>
        <SkeletonTable dense rows={spec.rows} columns={spec.columns} label={label} />
      </div>
    </div>
  );
}

/**
 * A heading, an add form and bordered rows — the shape of a «تعریف‌ها» view.
 *
 * The title renders FOR REAL rather than as a block, so the page keeps its
 * identity through the wait. A `spec.form` of `null` means the screen opens with
 * a boxed 2×2 form instead of an inline row (that is «دوره‌ای»'s shape).
 */
export function SetupSurface({ spec, title, label }) {
  return (
    <div style={{ flex: 1, overflow: 'hidden', padding: PANEL_PAD.regular }}>
      <div style={{ maxWidth: spec.maxWidth }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.textPrimary }}>{title}</h2>
        <div aria-hidden style={{ margin: '6px 0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="72%" height={12} />
          <Skeleton width="48%" height={12} />
        </div>
        {spec.form ? (
          <div aria-hidden style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
            {spec.form.map((f, i) => (
              <SkeletonInput key={i} width={f.width} style={f.flex ? { flex: 1 } : undefined} />
            ))}
            <SkeletonButton width={96} />
          </div>
        ) : (
          <div aria-hidden style={{ padding: 12, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {Array.from({ length: 4 }, (_, i) => <SkeletonInput key={i} />)}
            </div>
            <div style={{ marginBottom: 10 }}><SkeletonInput /></div>
            <SkeletonButton width={140} />
          </div>
        )}
        <SkeletonRegion label={label}>
          {Array.from({ length: spec.rows }, (_, i) => <SetupRowSkeleton key={i} actions={spec.actions} />)}
        </SkeletonRegion>
      </div>
    </div>
  );
}

/** The phone's `MobileSectionNav` strip: tab labels over a bottom rule. */
export function MobileSectionStrip({ widths = [64, 52, 48] }) {
  return (
    <div aria-hidden style={{ display: 'flex', gap: 4, padding: '0 0.25rem', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
      {widths.map((w, i) => (
        <div key={i} style={{ padding: '12px 14px' }}><Skeleton width={w} height={12} /></div>
      ))}
    </div>
  );
}

/** The shared page frame: a labelled region that fills the viewport. */
export const WORKBENCH_ROOT = {
  display: 'flex', flexDirection: 'column', height: '100%',
  background: C.bg, overflow: 'hidden',
};

export const WORKBENCH_HEADER = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 16, flexWrap: 'wrap', padding: '14px 20px',
  background: C.card, borderBottom: `1px solid ${C.border}`, flexShrink: 0,
};

export const WORKBENCH_MAIN = {
  flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: C.card,
};
